import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { Theme } from '../theme';
import { Icon, type IconName } from './Icon';

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  variant?: AlertVariant;
  title?: React.ReactNode;
  onClose?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const ICON: Record<AlertVariant, IconName> = { info: 'info', success: 'success', warning: 'warn', danger: 'danger' };

const tones = (t: Theme) => ({
  info:    css`background: ${t.color.blue[50]};  border-color: ${t.color.blue[200]};  color: ${t.color.blue[800]};`,
  success: css`background: ${t.color.green[50]}; border-color: ${t.color.green[200]}; color: ${t.color.green[700]};`,
  warning: css`background: ${t.color.amber[50]}; border-color: ${t.color.amber[200]}; color: ${t.color.amber[700]};`,
  danger:  css`background: ${t.color.red[50]};   border-color: ${t.color.red[200]};   color: ${t.color.red[700]};`,
});

const Root = styled.div<{ $variant: AlertVariant }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.base};
  border: 1px solid;
  font-size: ${({ theme }) => theme.fontSize.base};
  ${({ theme, $variant }) => tones(theme)[$variant]}
`;
const Body = styled.div`flex: 1;`;
const Title = styled.p`font-weight: ${({ theme }) => theme.fontWeight.semibold}; margin: 0 0 2px; font-size: ${({ theme }) => theme.fontSize.base};`;
const Text = styled.p`color: ${({ theme }) => theme.color.fg.muted}; margin: 0; font-size: ${({ theme }) => theme.fontSize.sm};`;
const Close = styled.button`
  margin-left: auto;
  border: 0; background: none; cursor: pointer;
  color: ${({ theme }) => theme.color.fg.subtle};
  width: 20px; height: 20px; display: grid; place-items: center;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover { background: rgba(0,0,0,0.05); }
`;

/** Lumen inline alert — persistent in-flow status message. */
export function Alert({ variant = 'info', title, onClose, children, className }: AlertProps) {
  return (
    <Root $variant={variant} className={className}>
      <Icon name={ICON[variant]} size={18} style={{ marginTop: 1, flexShrink: 0 }} />
      <Body>
        {title && <Title>{title}</Title>}
        {children && <Text>{children}</Text>}
      </Body>
      {onClose && <Close onClick={onClose} aria-label="Dismiss"><Icon name="x" size={14} /></Close>}
    </Root>
  );
}
